import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Catalog } from '@cureskin/api-client/src/server-api';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { CartService } from '@services/cart-service/cart.service';
import { BroadcastService } from '@services/broadcast-service';
import { EventLoggerService } from '@services/event-logger-service';

export interface PregnancyRestrictionBottomSheetPayload {
  product: Catalog,
  navigateToCartOnAdd?: boolean,
  navigateToArticlePage?: boolean,
  section: string,
}

@Component({
  selector: 'pregnancy-restriction-bottomsheet',
  imports: [ButtonCtaComponent],
  templateUrl: './pregnancy-restriction-bottomsheet.component.html',
})
export class PregnancyRestrictionBottomsheetComponent implements OnInit, OnDestroy {
  private readonly eventLoggerService = inject(EventLoggerService);
  public readonly data: PregnancyRestrictionBottomSheetPayload = inject(MAT_BOTTOM_SHEET_DATA);
  bottomSheetRef: MatBottomSheetRef<PregnancyRestrictionBottomsheetComponent> = inject(MatBottomSheetRef<PregnancyRestrictionBottomsheetComponent>);
  broadcastService: BroadcastService = inject(BroadcastService);
  cartService: CartService = inject(CartService);
  private readonly router = inject(Router);

  productImage: string | null = null;
  private dismissSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'add_to_cart_pregnancy_bs',
    }));
    this.productImage = this.getProductImage(this.data.product);
    this.dismissSubscription = this.broadcastService.on('DISMISS_PREGNANCY_BOTTOMSHEET').subscribe((): void => {
      this.bottomSheetRef.dismiss();
    });
  }

  ngOnDestroy(): void {
    this.dismissSubscription?.unsubscribe();
  }


  getProductImage(product: Catalog): string | null {
    return product?.rebrandedImageWithBackground?.at(-1)
    ?? product?.productUnboxedImage?.at(-1)
    ?? null;
  }

  closeBottomSheet(): void {
    this.bottomSheetRef.dismiss();
  }

  async addToCart(): Promise<void> {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
      name: 'add_to_cart_anyway_pregnancy_bs',
    }));
    const result = await this.cartService.addOrRemoveFromCart(
      this.data.product, 
      'ADD',
      { 
        productsInCart: {},
        section: this.data?.section,
        overridePregnancyCheck: true,
      },
    );
    if (result.success) {
      this.broadcastService.broadcast('REFRESH_SHOP_CART', { productsInCart: result.updatedProductsInCart });
      this.bottomSheetRef.dismiss();
      if (this.data.navigateToCartOnAdd) {
        this.router.navigate(['cart']);
      }
    }
  }

  async navigateToArticle(): Promise<void> {
    await this.router.navigate([`/product/${this.data.product.objectId}`], { queryParams: { section: this.data.section } });
    this.bottomSheetRef.dismiss();
  }
}
